import {
  defaultTheme,
  ErrorBoundary,
  YesErrorTracking
} from '@yes.technology/react-toolkit'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { TranslationContainer } from 'react-i18n-lite'
import { Provider as ReduxProvider } from 'react-redux'
import {
  createBrowserRouter,
  RouterProvider,
  useLocation
} from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import { ThemeProvider } from 'styled-components'

import ApplicationContainer from 'infrastructure/Application/Container'
import apiAsync from 'infrastructure/shared/api/api-async'
import validateConfigUrls from 'infrastructure/shared/utils/validateConfigUrls'
import ResizeDetector from 'shared/components/ResizeDetector'
import { tagManager } from 'shared/utils'
import store, { persistor } from 'state/store'

import reportWebVitals from './reportWebVitals'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { APIProvider as GoogleMapsProvider } from '@vis.gl/react-google-maps'
import GenericErrorPage from 'infrastructure/GenericErrorPage'
import sanitizeSW from 'infrastructure/ServiceWorkerWrapper/utils/sanitizeServiceWorker'
import SiteRoutes from 'site/Routes'
import locales from './locales'

validateConfigUrls(window.apiUrls)
apiAsync.setConfig(window.apiUrls)
apiAsync.setDispatch(store.dispatch)

YesErrorTracking({
  service: 'sentry',
  publicKey: window.sentryDNS,
  traceTargets: window.trackingUrls
})

tagManager.initialize(window.tagManagerArgs)

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

function renderError() {
  return <GenericErrorPage />
}

const LayoutComponent = () => {
  const { pathname } = useLocation()

  return (
    <ResizeDetector>
      <TranslationContainer locales={locales} defaultLanguage='pt-BR'>
        <HelmetProvider>
          <ErrorBoundary renderOnError={renderError}>
            <GoogleMapsProvider apiKey={window.googleCloudApikey}>
              <ApplicationContainer key={pathname} />
            </GoogleMapsProvider>
          </ErrorBoundary>
        </HelmetProvider>
      </TranslationContainer>
    </ResizeDetector>
  )
}

const router = createBrowserRouter([
  {
    element: <LayoutComponent />,
    children: SiteRoutes
  }
])

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30000
    }
  }
})

root.render(
  <React.StrictMode>
    <ErrorBoundary renderOnError={renderError}>
      <ReduxProvider store={store}>
        <ThemeProvider theme={defaultTheme}>
          <QueryClientProvider client={queryClient}>
            <PersistGate loading={null} persistor={persistor}>
              <RouterProvider router={router} />
            </PersistGate>
          </QueryClientProvider>
        </ThemeProvider>
      </ReduxProvider>
    </ErrorBoundary>
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()

// registerServiceWorker()
sanitizeSW()
